import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Icon,
  Input,
  Row,
  Spin,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import moment from 'moment';
import { apiGet } from './utils/apiService';

const { RangePicker } = DatePicker;

const SALE_PAGE_SIZE = 200;
const ASSET_PAGE_SIZE = 5000;
const ASSET_MAX_PAGES = 20;

const DEFAULT_RANGE = () => [moment().subtract(3, 'months').startOf('day'), moment().endOf('day')];

const idOf = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return raw._id || raw.id || null;
  return null;
};

const nameOf = (raw, fallback = '') => {
  if (!raw) return fallback;
  if (typeof raw === 'string') return fallback;
  if (typeof raw === 'object') return raw.name || raw.code || raw.altName || fallback;
  return fallback;
};

const extract = (res) => {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.assets)) return d.assets;
  if (d && Array.isArray(d.results)) return d.results;
  if (d && Array.isArray(d.rows)) return d.rows;
  if (d && Array.isArray(d.docs)) return d.docs;
  return [];
};

const buildSaleDateFilter = (from, to) =>
  JSON.stringify([
    JSON.stringify({
      value: [from, to],
      type: 'Date',
      field: 'saleDate',
    }),
  ]);

const toCsv = (columns, rows) => {
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headers = columns.map((c) => c.csvTitle || c.title).map(escape).join(',');
  const body = rows
    .map((r) => columns.map((c) => escape(c.csv ? c.csv(r) : r[c.dataIndex])).join(','))
    .join('\n');
  return `${headers}\n${body}`;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

function SalesAssetReport() {
  const [range, setRange] = useState(DEFAULT_RANGE());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [vouchersCount, setVouchersCount] = useState(0);
  const [queriedRange, setQueriedRange] = useState(null);

  // Caches that survive within a single fetch run (cleared on new fetch).
  const locationCache = useRef(new Map());
  const clientCache = useRef(new Map());

  const fetchAllAssets = useCallback(async () => {
    const byId = new Map();
    for (let p = 0; p < ASSET_MAX_PAGES; p += 1) {
      const offset = p * ASSET_PAGE_SIZE;
      // eslint-disable-next-line no-await-in-loop
      const res = await apiGet(`/asset/asset-all?limit=${ASSET_PAGE_SIZE}&offset=${offset}`, {
        headers: { Accept: '*/*' },
      });
      if (res && res.success === false) {
        if (p === 0) throw new Error(res.error || 'Failed to load asset list');
        break;
      }
      const page = extract(res);
      page.forEach((a) => {
        const id = a && (a._id || a.id);
        if (id) byId.set(String(id), a);
      });
      if (page.length < ASSET_PAGE_SIZE) break;
    }
    return byId;
  }, []);

  const fetchAllSales = useCallback(async (fromIso, toIso) => {
    const filterParam = encodeURIComponent(buildSaleDateFilter(fromIso, toIso));
    const all = [];
    let offset = 0;
    // Safety cap: 200 pages * 200 = 40k vouchers in a single range.
    for (let i = 0; i < 200; i += 1) {
      const url = `/sale-order?limit=${SALE_PAGE_SIZE}&offset=${offset}&filters=${filterParam}`;
      // eslint-disable-next-line no-await-in-loop
      const res = await apiGet(url, { headers: { Accept: 'application/json' } });
      if (res && res.success === false) {
        throw new Error(res.error || 'Failed to load sale orders');
      }
      const page = extract(res);
      all.push(...page);
      setProgress(`Fetched ${all.length} sale vouchers...`);
      if (page.length < SALE_PAGE_SIZE) break;
      offset += SALE_PAGE_SIZE;
    }
    return all;
  }, []);

  const resolveLocationName = useCallback(async (locRef) => {
    if (!locRef) return '';
    if (typeof locRef === 'object') return nameOf(locRef);
    const id = String(locRef);
    if (locationCache.current.has(id)) return locationCache.current.get(id);
    locationCache.current.set(id, '');
    try {
      const res = await apiGet(`/location/${id}`, { headers: { Accept: '*/*' } });
      if (res && res.success && res.data) {
        const name = res.data.name || res.data.code || res.data.title || '';
        locationCache.current.set(id, name);
        return name;
      }
    } catch (e) {
      // ignore
    }
    return '';
  }, []);

  const resolveClientName = useCallback(async (cliRef) => {
    if (!cliRef) return '';
    if (typeof cliRef === 'object') return nameOf(cliRef);
    const id = String(cliRef);
    if (clientCache.current.has(id)) return clientCache.current.get(id);
    clientCache.current.set(id, '');
    try {
      const res = await apiGet(`/client/${id}`, { headers: { Accept: '*/*' } });
      if (res && res.success && res.data) {
        const name = res.data.name || res.data.code || res.data.title || '';
        clientCache.current.set(id, name);
        return name;
      }
    } catch (e) {
      // ignore
    }
    return '';
  }, []);

  const handleFetch = useCallback(async () => {
    if (!range || !range[0] || !range[1]) {
      message.warning('Pick a date range.');
      return;
    }
    const from = range[0].toISOString();
    const to = range[1].toISOString();

    setLoading(true);
    setError(null);
    setRows([]);
    setVouchersCount(0);
    setProgress('Loading asset master...');
    locationCache.current = new Map();
    clientCache.current = new Map();

    try {
      const [assetById, sales] = await Promise.all([
        fetchAllAssets().catch((e) => {
          console.warn('[SalesAssetReport] asset master failed, continuing without it', e);
          return new Map();
        }),
        fetchAllSales(from, to),
      ]);

      // Collect unique location and client IDs to resolve in parallel.
      const locIds = new Set();
      const cliIds = new Set();
      sales.forEach((sale) => {
        (sale.assets || []).forEach((a) => {
          const lid = idOf(a.location);
          if (lid) locIds.add(lid);
          const cid = idOf(a.client);
          if (cid) cliIds.add(cid);
        });
      });

      setProgress(`Resolving ${locIds.size} locations and ${cliIds.size} clients...`);
      await Promise.all([
        ...Array.from(locIds).map((id) => resolveLocationName(id)),
        ...Array.from(cliIds).map((id) => resolveClientName(id)),
      ]);

      const flat = [];
      sales.forEach((sale) => {
        const soNo = sale.soNo || '';
        const saleDate = sale.saleDate || sale.createdAt || null;
        const vendorName = nameOf(sale.vendor, 'Unknown');
        const vendorId = idOf(sale.vendor);
        (sale.assets || []).forEach((a, idx) => {
          const assetId = idOf(a.asset);
          const masterAsset = assetId ? assetById.get(String(assetId)) : null;
          const parentAssetId = idOf(a.parentAsset);
          const parentMaster = parentAssetId ? assetById.get(String(parentAssetId)) : null;
          const locId = idOf(a.location);
          const cliId = idOf(a.client);
          flat.push({
            key: `${sale._id}-${idx}`,
            saleId: sale._id,
            soNo,
            saleDate,
            vendorName,
            vendorId,
            assetId,
            assetCode: (masterAsset && (masterAsset.assetCode || masterAsset.code)) || '',
            altName: (masterAsset && masterAsset.altName) || '',
            assetModel:
              (masterAsset && (nameOf(masterAsset.assetModel) || masterAsset.modelName)) || '',
            parentAssetCode:
              (parentMaster && (parentMaster.assetCode || parentMaster.code)) || '',
            location: locId ? locationCache.current.get(String(locId)) || '' : '',
            subLocation: cliId ? clientCache.current.get(String(cliId)) || '' : '',
            rate: Number(a.rate) || 0,
            remark: a.remark || '',
          });
        });
      });

      setRows(flat);
      setVouchersCount(sales.length);
      setQueriedRange([range[0].clone(), range[1].clone()]);
      setProgress('');
      if (sales.length === 0) {
        message.info('No sale vouchers found in this range.');
      }
    } catch (e) {
      console.error('[SalesAssetReport] fetch failed', e);
      setError(e.message || 'Failed to load sales report');
    } finally {
      setLoading(false);
      setProgress('');
    }
  }, [range, fetchAllAssets, fetchAllSales, resolveLocationName, resolveClientName]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.soNo, r.vendorName, r.assetCode, r.altName, r.location, r.subLocation, r.parentAssetCode, r.remark]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const stats = useMemo(() => {
    const totalValue = filteredRows.reduce((s, r) => s + (r.rate || 0), 0);
    const uniqueVouchers = new Set(filteredRows.map((r) => r.saleId)).size;
    return {
      totalValue,
      uniqueVouchers,
      assetCount: filteredRows.length,
    };
  }, [filteredRows]);

  const columns = [
    {
      title: 'Sale Voucher',
      dataIndex: 'soNo',
      key: 'soNo',
      width: 120,
      fixed: 'left',
      sorter: (a, b) => String(a.soNo).localeCompare(String(b.soNo)),
      render: (v) => v || '—',
    },
    {
      title: 'Sale Date',
      dataIndex: 'saleDate',
      key: 'saleDate',
      width: 130,
      sorter: (a, b) => moment(a.saleDate || 0).valueOf() - moment(b.saleDate || 0).valueOf(),
      defaultSortOrder: 'descend',
      render: (d) => (d ? moment(d).format('YYYY-MM-DD') : '—'),
      csv: (r) => (r.saleDate ? moment(r.saleDate).format('YYYY-MM-DD HH:mm') : ''),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 160,
      sorter: (a, b) => String(a.vendorName).localeCompare(String(b.vendorName)),
      render: (v) => v || '—',
    },
    {
      title: 'Asset Code',
      dataIndex: 'assetCode',
      key: 'assetCode',
      width: 140,
      sorter: (a, b) => String(a.assetCode).localeCompare(String(b.assetCode)),
      render: (v, r) =>
        v ? (
          <span style={{ fontFamily: 'monospace' }}>{v}</span>
        ) : (
          <Tag color="orange" title={`Asset ID ${r.assetId || ''}`}>Unknown</Tag>
        ),
      csv: (r) => r.assetCode || r.assetId || '',
    },
    {
      title: 'Asset Name',
      dataIndex: 'altName',
      key: 'altName',
      width: 200,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Parent Asset',
      dataIndex: 'parentAssetCode',
      key: 'parentAssetCode',
      width: 130,
      render: (v) => v || '—',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 160,
      sorter: (a, b) => String(a.location).localeCompare(String(b.location)),
      render: (v) => v || '—',
    },
    {
      title: 'Sub-location',
      dataIndex: 'subLocation',
      key: 'subLocation',
      width: 160,
      render: (v) => v || '—',
    },
    {
      title: 'Rate (₹)',
      dataIndex: 'rate',
      key: 'rate',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.rate || 0) - (b.rate || 0),
      render: (v) => (Number(v) || 0).toLocaleString('en-IN'),
      csv: (r) => r.rate || 0,
    },
    {
      title: 'Remark',
      dataIndex: 'remark',
      key: 'remark',
      width: 220,
      ellipsis: true,
      render: (v) => v || '—',
    },
  ];

  const handleExport = useCallback(() => {
    if (!filteredRows.length) {
      message.warning('Nothing to export.');
      return;
    }
    const csv = toCsv(columns, filteredRows);
    const from = queriedRange ? queriedRange[0].format('YYYYMMDD') : 'na';
    const to = queriedRange ? queriedRange[1].format('YYYYMMDD') : 'na';
    downloadCsv(`sales_assets_${from}_${to}.csv`, csv);
  }, [filteredRows, columns, queriedRange]);

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <span>
            <Icon type="shopping-cart" /> Sales Asset Report
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16} type="flex" align="middle">
          <Col>
            <span style={{ marginRight: 8, color: '#666' }}>Sale date range:</span>
            <RangePicker
              value={range}
              onChange={(v) => setRange(v)}
              format="YYYY-MM-DD"
              allowClear={false}
              ranges={{
                Today: [moment().startOf('day'), moment().endOf('day')],
                'Last 7 Days': [moment().subtract(7, 'days').startOf('day'), moment().endOf('day')],
                'Last 30 Days': [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')],
                'This Month': [moment().startOf('month'), moment().endOf('month')],
                'Last 3 Months': [moment().subtract(3, 'months').startOf('day'), moment().endOf('day')],
                'This FY': [moment().month(3).startOf('month'), moment().endOf('day')],
              }}
            />
          </Col>
          <Col>
            <Button type="primary" icon="search" loading={loading} onClick={handleFetch}>
              Fetch
            </Button>
          </Col>
          <Col>
            <Button icon="download" disabled={!filteredRows.length} onClick={handleExport}>
              Export CSV
            </Button>
          </Col>
          <Col flex="auto" style={{ marginLeft: 'auto' }}>
            <Input.Search
              allowClear
              placeholder="Search asset code, vendor, location, remark…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 360 }}
            />
          </Col>
        </Row>

        {progress && (
          <div style={{ marginTop: 12, color: '#888' }}>
            <Spin size="small" style={{ marginRight: 8 }} />
            {progress}
          </div>
        )}

        {error && (
          <Alert
            type="error"
            showIcon
            message="Failed to load sales report"
            description={error}
            style={{ marginTop: 12 }}
            closable
            onClose={() => setError(null)}
          />
        )}
      </Card>

      {rows.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={6}>
              <Statistic title="Sale Vouchers" value={stats.uniqueVouchers} />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic title="Assets Sold" value={stats.assetCount} />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="Total Value"
                value={stats.totalValue}
                precision={0}
                prefix="₹"
                formatter={(v) => Number(v).toLocaleString('en-IN')}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="Range"
                valueRender={() =>
                  queriedRange
                    ? `${queriedRange[0].format('DD MMM YY')} – ${queriedRange[1].format('DD MMM YY')}`
                    : '—'
                }
              />
            </Col>
          </Row>
        </Card>
      )}

      <Card
        title={
          <span>
            Assets Sold{' '}
            <Tag style={{ marginLeft: 8 }}>{filteredRows.length}</Tag>
            {vouchersCount > 0 && (
              <span style={{ color: '#999', fontWeight: 'normal', marginLeft: 8 }}>
                across {stats.uniqueVouchers} voucher{stats.uniqueVouchers === 1 ? '' : 's'}
              </span>
            )}
          </span>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredRows}
          loading={loading}
          size="middle"
          scroll={{ x: 1500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
        />
      </Card>
    </div>
  );
}

export default SalesAssetReport;
